/** @jsx jsx */
import { Flex, Loader } from "@eworkit/iris";
import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { useDialogState } from "@/components/dialogs/useDialogState";
import { isTimeReportEditable } from "@/data/time-reports";
import { useTimeReportQuery } from "@/data/time-reports/queries/useTimeReportQuery";
import { withRouteProps } from "@/infrastructure/Route";
import { useToasts } from "@/views/components/Toast/useToasts";

import { EditTimeReportModal } from "../edit";
import { PreviewTimeReportModal } from "../preview";
import { SubmitTimeReportModal } from "../submit";
import { TimeReportDetailsHeader } from "./_components/TimeReportDetailsHeader";
import { TimeReportDetailsTabPanels } from "./_components/TimeReportDetailsTabPanels";
import { TimeReportDetailsRouteProps, transformRouteProps } from "./selectors";

interface TimeReportDetailsViewComponentProps extends TimeReportDetailsRouteProps {}

export const TimeReportDetailsViewComponent: FC<TimeReportDetailsViewComponentProps> = ({ routeProps }) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const { data, refetch, isFetching } = useTimeReportQuery(routeProps.timeReportGuid);

  const refetchTimeReport = useCallback(async () => {
    const newData = await refetch();

    if (newData.data && isTimeReportEditable(data) && !isTimeReportEditable(newData.data)) {
      addToast({
        text: t("TIME_REPORTS.SUBMIT.CANNOT_BE_SUBMITTED_ERROR_MESSAGE"),
        type: "warning",
      });
    }
  }, [data, refetch, addToast, t]);

  const editModalState = useDialogState({
    beforeShow: refetchTimeReport,
    afterHide: refetchTimeReport,
  });

  const previewModalState = useDialogState({
    beforeShow: refetchTimeReport,
  });

  const submitModalState = useDialogState();

  const handleEditModalSubmitClick = useCallback(() => {
    editModalState.hide();
    previewModalState.show();
  }, [editModalState, previewModalState]);

  const handlePreviewModalEditClick = useCallback(() => {
    previewModalState.hide();
    editModalState.show();
  }, [previewModalState, editModalState]);

  const handlePreviewModalSubmitClick = useCallback(() => {
    submitModalState.show();
  }, [submitModalState]);

  return isFetching ? (
    <Flex alignItems="center" justifyContent="center" my="xl">
      <Loader size="xl" color="vg500" />
    </Flex>
  ) : (
    <React.Fragment>
      <Flex flexDirection="column">
        <TimeReportDetailsHeader
          timeReport={data}
          editModalState={editModalState}
          previewModalState={previewModalState}
        />
        <TimeReportDetailsTabPanels timeReport={data} />
      </Flex>

      {isTimeReportEditable(data) && (
        <React.Fragment>
          {editModalState.visible && (
            <EditTimeReportModal
              dialogState={editModalState}
              timeReport={data}
              refetchTimeReport={refetchTimeReport}
              onSubmitClick={handleEditModalSubmitClick}
            />
          )}

          {previewModalState.visible && (
            <PreviewTimeReportModal
              dialogState={previewModalState}
              timeReport={data}
              onEditClick={handlePreviewModalEditClick}
              onSubmitClick={handlePreviewModalSubmitClick}
            />
          )}

          {submitModalState.visible && (
            <SubmitTimeReportModal
              dialogState={submitModalState}
              timeReport={data}
              refetchTimeReport={refetchTimeReport}
            />
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export const TimeReportDetailsView = withRouteProps(TimeReportDetailsViewComponent, transformRouteProps);
